import type { PropsWithChildren } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { createReadableClass } from '@elfsight/widget-utils';
import Marquee from 'react-fast-marquee';
import type { PropsWithClassName } from '../../../../types';
import { useSource } from '../../sources';

const TICKER_DELAY_SECONDS = 4;
const TICKER_SPEED = 50;

interface TrackInfoProps extends PropsWithChildren<PropsWithClassName> {
  disabled?: boolean;
}

export function Ticker({
  className,
  children,
  disabled = false
}: TrackInfoProps) {
  const { currentItemId } = useSource();

  const [isMarqueePlaying, setIsMarqueePlaying] = useState(true);
  const [needsMarquee, setNeedsMarquee] = useState(false);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const checkIfNeedsMarquee = useCallback(() => {
    if (!contentRef.current || !containerRef.current) {
      return;
    }

    setNeedsMarquee(
      !disabled &&
        containerRef.current.offsetWidth < contentRef.current.offsetWidth
    );
  }, [disabled]);

  useEffect(() => {
    if (!contentRef.current) {
      return;
    }

    resizeObserverRef.current = new ResizeObserver(checkIfNeedsMarquee);
    resizeObserverRef.current.observe(contentRef.current);
    window.addEventListener('resize', checkIfNeedsMarquee);
    checkIfNeedsMarquee();

    return () => {
      resizeObserverRef.current?.disconnect();
      window.removeEventListener('resize', checkIfNeedsMarquee);
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [contentRef.current, needsMarquee, checkIfNeedsMarquee]);

  const handleVisibilityChange = useCallback(() => {
    const previousNeedsMarquee = needsMarquee;
    setNeedsMarquee(false);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    document.addEventListener(
      'visibilitychange',
      () => {
        setNeedsMarquee(previousNeedsMarquee);
        setIsMarqueePlaying(true);
      },
      { once: true }
    );
  }, [needsMarquee]);

  // Сбрасываем анимацию при смене вкладки
  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleVisibilityChange]);

  const handleCycleComplete = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setIsMarqueePlaying(false);
    timeoutRef.current = setTimeout(() => {
      setIsMarqueePlaying(true);
    }, TICKER_DELAY_SECONDS * 1000);
  }, [setIsMarqueePlaying]);

  if (!needsMarquee) {
    return (
      <Container ref={containerRef} className={className}>
        <Content ref={contentRef} _disabled={disabled}>
          {children}
        </Content>
      </Container>
    );
  }

  return (
    <Container ref={containerRef} className={className}>
      <StyledMarquee
        key={currentItemId}
        speed={TICKER_SPEED}
        delay={TICKER_DELAY_SECONDS}
        onCycleComplete={handleCycleComplete}
        play={isMarqueePlaying}
      >
        <Content ref={contentRef} _disabled={disabled}>
          {children}
        </Content>
      </StyledMarquee>
    </Container>
  );
}

const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'track-info',
    element: 'ticker-container'
  })
})``;

const Content = styled.div.attrs<{
  _disabled: boolean;
}>({
  className: createReadableClass({
    component: 'track-info',
    element: 'ticker-content'
  })
})`
  ${({ _disabled }) =>
    !_disabled &&
    css`
      width: fit-content;

      & * {
        width: fit-content;
      }
    `}
`;

const StyledMarquee = styled(Marquee).attrs({
  className: createReadableClass({
    component: 'track-info',
    element: 'ticker'
  })
})`
  overflow: hidden;

  // Нужно чтобы добавить отступ в конце
  & .rfm-marquee::after {
    content: '';
    display: block;
    width: 40px;
  }
`;
